import { DestroyRef, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl } from '@angular/forms';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

export const SEARCH_DEBOUNCE_MS = 400;

/**
 * Search box bound to a controlled filter value. Typing emits the trimmed text after a pause; external changes
 * (URL, "clear filters") overwrite the box, but our own echoes do not, so typing is never clobbered.
 * Must be called in an injection context (a field initializer or the constructor).
 */
export function createSearchBox(current: () => string, emit: (search: string) => void): FormControl<string> {
  const destroyRef = inject(DestroyRef);
  const control = new FormControl<string>('', { nonNullable: true });
  let lastEmitted = '';

  control.valueChanges.pipe(debounceTime(SEARCH_DEBOUNCE_MS), distinctUntilChanged(), takeUntilDestroyed(destroyRef)).subscribe((value) => {
    const search = value.trim();
    if (search === current()) return;
    lastEmitted = search;
    emit(search);
  });

  effect(() => {
    const search = current();
    if (search !== lastEmitted) {
      lastEmitted = search;
      control.setValue(search, { emitEvent: false });
    }
  });

  return control;
}
